import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserActions } from '@repo/domain-service';
import { TestId } from '@repo/test-id';
import { tr } from '@repo/locale';
import { Button } from '@repo/ui';

import { IMPERSONATION_TOKEN_QUERY_PARAM } from '@features/auth/constants/impersonationRoute';
import { logout } from '@features/auth/services/authService';
import { useAuthStore } from '@features/auth/stores/useAuthStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

export default function ImpersonationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumeImpersonationToken } = useUserActions();
  const setImpersonating = useAuthStore((state) => state.setImpersonating);

  const [failed, setFailed] = useState<boolean>(false);
  const consumedRef = useRef<boolean>(false);

  const token = searchParams.get(IMPERSONATION_TOKEN_QUERY_PARAM) ?? undefined;
  const testId = TestId.root('impersonationPage');

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;

    if (!token) {
      setFailed(true);
      return;
    }

    const impersonate = async () => {
      logout();
      setImpersonating(true);

      try {
        await consumeImpersonationToken({ token });
        navigate(ROUTE_PATHS[RouteName.ACCOUNT], { replace: true });
      } catch {
        logout();
        setFailed(true);
      }
    };

    void impersonate();
  }, [token, consumeImpersonationToken, setImpersonating, navigate]);

  const handleBack = () => {
    navigate(ROUTE_PATHS[RouteName.MAP], { replace: true });
  };

  if (failed) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center" data-testid={testId}>
        <h1 className="text-heading text-xl font-semibold">{tr('auth:impersonation.error.title')}</h1>
        <p className="text-body max-w-md text-sm">{tr('auth:impersonation.error.description')}</p>
        <Button
          testId={testId.append('backButton')}
          label={tr('auth:impersonation.error.back')}
          color="brand"
          onClick={handleBack}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center" data-testid={testId}>
      <p className="text-body text-sm">{tr('auth:impersonation.loading')}...</p>
    </div>
  );
}
